import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuControlsOption} from '@Components/contextual-menu/components/contextual-menu-controls-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useAppSelector} from '@/hooks';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';

enum PosterItemMovementControlsOptions {
  TO_BOTTOM = 'to_bottom',
  BACKWARD = 'backward',
  TO_TOP = 'to_top',
  FORWARD = 'forward',
}

interface ItemMovementOptionsProps {
  className?: string;
}

export function ItemMovementOptions({className = ''}: ItemMovementOptionsProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const zIndex = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsOrder.indexOf(activeItemId);
  });
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const isLocked = useGetKeyFromActiveItem('lockMovementX');

  if (!activeItem) {
    throw new Error('Active item is null - unable to show item movement options');
  }

  const onClickBackward = (): void => {
    currentPage?.items.sendBackwardsSelectedItem();
  };
  const onClickForward = (): void => {
    currentPage?.items.bringForwardSelectedItem();
  };
  const onClickToBottom = (): void => {
    currentPage?.items.sendToBackSelectedItem();
  };
  const onClickToTop = (): void => {
    currentPage?.items.bringToFrontSelectedItem();
  };

  const onClickHandler = (clickedId: PosterItemMovementControlsOptions): void => {
    switch (clickedId) {
      case PosterItemMovementControlsOptions.BACKWARD: {
        onClickBackward();
        return;
      }
      case PosterItemMovementControlsOptions.FORWARD: {
        onClickForward();
        return;
      }
      case PosterItemMovementControlsOptions.TO_BOTTOM: {
        onClickToBottom();
        return;
      }
      case PosterItemMovementControlsOptions.TO_TOP: {
        onClickToTop();
        return;
      }
      default:
        throw new Error('unhandled option for item movement');
    }
  };
  return (
    <ContextualMenuControlsOption
      key={zIndex}
      id="item-position-movement-options"
      onClick={onClickHandler}
      itemsClassName="_full-width"
      containerClassName={`${className}  flex-justify-between`}
      data={[
        {
          id: PosterItemMovementControlsOptions.TO_BOTTOM,
          icon: 'icon-to-back',
          text: window.i18next.t('pmwjs_to_bottom'),
          disabled: !currentPage?.items.canSelectedItemsBeMovedBackwards() || isLocked,
        },
        {
          id: PosterItemMovementControlsOptions.BACKWARD,
          icon: 'icon-send-to-back',
          text: window.i18next.t('pmwjs_backward'),
          disabled: !currentPage?.items.canSelectedItemsBeMovedBackwards() || isLocked,
        },
        {
          id: PosterItemMovementControlsOptions.FORWARD,
          icon: 'icon-send-to-front',
          text: window.i18next.t('pmwjs_forward'),
          disabled: !currentPage?.items.canSelectedItemsBeMovedForward() || isLocked,
        },
        {
          id: PosterItemMovementControlsOptions.TO_TOP,
          icon: 'icon-to-front',
          text: window.i18next.t('pmwjs_to_top'),
          disabled: !currentPage?.items.canSelectedItemsBeMovedForward() || isLocked,
        },
      ]}
    />
  );
}
